"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { MapPin, Phone, Mail, ChevronUp, Send, Loader2 } from "lucide-react";
import Image from "next/image";
import { useSettings } from "@/context/SettingsContext";
import { apiClient } from "@/lib/apiClient";
import toast from "react-hot-toast";

export default function Footer() {
  const settings = useSettings();
  const pathname = usePathname();
  const router = useRouter();
  const [services, setServices] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchDynamicData = async () => {
    try {
      const [servicesRes, projectsRes] = await Promise.all([
        apiClient.get('/public/services') as any,
        apiClient.get('/public/projects') as any
      ]);

      if (servicesRes.success && Array.isArray(servicesRes.data)) {
        setServices(servicesRes.data);
      }
      if (projectsRes.success && Array.isArray(projectsRes.data)) {
        setProjects(projectsRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch footer data", error);
    }
  };

  useEffect(() => {
    fetchDynamicData();
  }, [pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    // Add artificial delay for UX and to prevent multiple rapid clicks
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsLoading(false);

    // Redirect to contact page with email pre-filled
    router.push(`/contact?email=${encodeURIComponent(email)}`);
  };

  return (
    <footer className="bg-[#f8f9fa] relative overflow-hidden font-sans border-t border-gray-100">

      {/* ══════════════════════════════════════════
          1. NEWSLETTER SECTION
      ══════════════════════════════════════════ */}
      <div className="max-w-[1000px] mx-auto px-6 sm:px-12 pt-24 pb-16 text-center">
        <div className="flex flex-col md:flex-row items-center justify-center gap-6">
          <h2 className="text-[28px] sm:text-[34px] font-serif font-medium text-gray-900 text-center md:text-left">
            Join Our Trusted Advisory Network
          </h2>

          <form onSubmit={handleSubscribe} className="relative w-full max-w-[400px]">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="Your business email"
              className="w-full bg-white border border-gray-200 shadow-sm rounded-full py-3.5 sm:py-4 pl-4 sm:pl-6 pr-[110px] sm:pr-[140px] text-[14px] sm:text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 transition-colors disabled:opacity-70"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="absolute right-1.5 sm:right-2 top-1.5 sm:top-2 bottom-1.5 sm:bottom-2 bg-[#111827] hover:bg-black text-white rounded-full px-4 sm:px-6 text-[13px] sm:text-[14px] font-bold transition-colors disabled:opacity-70 flex items-center justify-center min-w-[90px] sm:min-w-[110px]"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
              ) : (
                "Get Access"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          2. MAIN FOOTER CONTENT (4 Columns)
      ══════════════════════════════════════════ */}
      <div className="max-w-[1200px] mx-auto px-6 sm:px-12 pb-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

        {/* Column 1: Logo & Contact */}
        <div className="flex flex-col gap-6">
          {/* Logo matches requested image or styling */}
          <Link href="/" className="mb-2 block">
            <Image
              src="/originalLogoTransparent.png"
              alt="Compare All Logo"
              width={220}
              height={80}
              className="h-20 w-auto max-w-[220px] object-contain mix-blend-multiply"
              style={{ width: 'auto', height: 'auto' }}
            />
          </Link>

          <ul className="space-y-4 text-[13px] text-gray-600 font-medium mt-4">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-900 shrink-0 mt-0.5" />
              <a href={`https://maps.google.com/?q=${encodeURIComponent(settings.address || '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#0d9488] transition-colors">{settings.address}</a>
            </li>
            {settings.contactPhone?.trim() && (
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-900 shrink-0" />
                <a href={`tel:${settings.contactPhone}`} className="hover:text-gray-900 transition-colors">{settings.contactPhone}</a>
              </li>
            )}
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-gray-900 shrink-0" />
              <a href={`mailto:${settings.contactEmail}`} className="hover:text-gray-900 transition-colors">{settings.contactEmail}</a>
            </li>
          </ul>
        </div>

        {/* Column 2: Services */}
        <div>
          <h4 role="heading" aria-level={2} className="text-[16px] font-bold text-gray-900 mb-6 font-serif">
            Services
          </h4>
          <ul className="space-y-3 text-[14px] text-gray-600 font-medium">
            {services.length > 0 ? services.map(service => (
              <li key={service._id}><Link href={`/services/${service.slug || service._id}`} className="hover:text-gray-900 transition-colors">{service.title}</Link></li>
            )) : (
              <>
                <li><Link href="/services" className="hover:text-gray-900 transition-colors">Procurement Strategy</Link></li>
                <li><Link href="/services" className="hover:text-gray-900 transition-colors">Commercial Negotiation</Link></li>
                <li><Link href="/services" className="hover:text-gray-900 transition-colors">IT Spend Review</Link></li>
                <li><Link href="/services" className="hover:text-gray-900 transition-colors">Contract Risk Advisory</Link></li>
                <li><Link href="/services" className="hover:text-gray-900 transition-colors">Vendor Management</Link></li>
              </>
            )}
          </ul>
        </div>

        {/* Column 3: Solutions */}
        <div>
          <h4 role="heading" aria-level={2} className="text-[16px] font-bold text-gray-900 mb-6 font-serif">
            Solutions
          </h4>
          <ul className="space-y-3 text-[14px] text-gray-600 font-medium">
            {projects.length > 0 ? projects.map(project => (
              <li key={project._id}><Link href={`/our-projects#${project.slug || project._id}`} className="hover:text-gray-900 transition-colors">{project.title}</Link></li>
            )) : (
              <>
                <li><Link href="/our-projects" className="hover:text-gray-900 transition-colors">Print Fleet Management</Link></li>
                <li><Link href="/our-projects" className="hover:text-gray-900 transition-colors">Mailroom Services</Link></li>
                <li><Link href="/our-projects" className="hover:text-gray-900 transition-colors">Office Equipment Sourcing</Link></li>
                <li><Link href="/our-projects" className="hover:text-gray-900 transition-colors">Telecoms & IT Integration</Link></li>
                <li><Link href="/our-projects" className="hover:text-gray-900 transition-colors">Document Management</Link></li>
              </>
            )}
          </ul>
        </div>

        {/* Column 4: Connect */}
        <div>
          <h4 role="heading" aria-level={2} className="text-[16px] font-bold text-gray-900 mb-6 font-serif">
            Connect
          </h4>

          {(() => {
            const hasSocial = (settings.socialLinks?.twitter && settings.socialLinks.twitter.length > 5) ||
              (settings.socialLinks?.facebook && settings.socialLinks.facebook.length > 5) ||
              (settings.socialLinks?.instagram && settings.socialLinks.instagram.length > 5) ||
              (settings.socialLinks?.linkedin && settings.socialLinks.linkedin.length > 5) ||
              (settings.socialLinks?.youtube && settings.socialLinks.youtube.length > 5) ||
              (settings.socialLinks?.whatsapp && settings.socialLinks.whatsapp.length > 5);

            return !hasSocial ? (
              <div className="mb-6">
                {settings.contactPhone?.trim() ? (
                  <a href={`tel:${settings.contactPhone}`} className="group inline-flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[13px] font-medium text-gray-700 shadow-sm hover:shadow-md hover:border-gray-300 hover:text-gray-900 transition-all duration-300 transform hover:-translate-y-0.5">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0d9488] opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#0d9488]"></span>
                    </span>
                    Talk to an Expert
                    <Phone className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#0d9488] transition-colors ml-1" />
                  </a>
                ) : (
                  <a href={`mailto:${settings.contactEmail}`} className="group inline-flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[13px] font-medium text-gray-700 shadow-sm hover:shadow-md hover:border-gray-300 hover:text-gray-900 transition-all duration-300 transform hover:-translate-y-0.5">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0d9488] opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#0d9488]"></span>
                    </span>
                    {settings.contactEmail}
                    <Mail className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#0d9488] transition-colors ml-1" />
                  </a>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 mb-6">
                {settings.socialLinks?.twitter && settings.socialLinks.twitter.length > 5 && (
                  <a href={settings.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" /></svg>
                  </a>
                )}
                {settings.socialLinks?.facebook && settings.socialLinks.facebook.length > 5 && (
                  <a href={settings.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>
                  </a>
                )}
                {settings.socialLinks?.instagram && settings.socialLinks.instagram.length > 5 && (
                  <a href={settings.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                  </a>
                )}
                {settings.socialLinks?.linkedin && settings.socialLinks.linkedin.length > 5 && (
                  <a href={settings.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                  </a>
                )}
                {settings.socialLinks?.youtube && settings.socialLinks.youtube.length > 5 && (
                  <a href={settings.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" /></svg>
                  </a>
                )}
                {settings.socialLinks?.whatsapp && settings.socialLinks.whatsapp.length > 5 && (
                  <a href={settings.socialLinks.whatsapp.startsWith('http') ? settings.socialLinks.whatsapp : `https://wa.me/${settings.socialLinks.whatsapp.replace(/[^0-9]/g, '')}?text=Hello%20Compare%20All%2C%20I%20would%20like%20to%20learn%20more%20about%20your%20services`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.898-4.45 9.898-9.898 0-5.447-4.45-9.897-9.898-9.897-5.447 0-9.897 4.45-9.897 9.897 0 2.115.6 3.737 1.621 5.438l-1.04 3.79 3.924-1.02zm3.766-7.391c-.347-.775-.714-.789-1.041-.803-.271-.011-.582-.011-.893-.011-.311 0-.816.117-1.244.582-.428.466-1.633 1.594-1.633 3.887 0 2.293 1.672 4.511 1.905 4.821.233.31 3.284 5.011 7.954 7.026 1.111.479 1.979.765 2.656.98.1.03.195.059.289.088.948.3 1.81.258 2.493.155.761-.115 2.333-.954 2.663-1.875.33-.922.33-1.713.232-1.875-.098-.162-.369-.258-.739-.443-.37-.184-2.188-1.08-2.528-1.205-.34-.125-.589-.185-.837.184-.249.369-.958 1.205-1.176 1.451-.218.246-.436.277-.806.092-.371-.184-1.564-.576-2.981-1.848-1.103-1-1.845-2.235-2.064-2.604-.218-.369-.023-.569.162-.754.166-.166.37-.431.555-.646.184-.216.246-.37.37-.616.124-.246.062-.462-.03-.646-.092-.185-.838-2.022-1.147-2.766z" /></svg>
                  </a>
                )}
              </div>
            );
          })()}

          <p className="text-[13px] text-gray-500 font-medium leading-relaxed pr-4 mt-6">
            {settings.companyDescription}
          </p>
        </div>

      </div>

      {/* ══════════════════════════════════════════
          3. BOTTOM COPYRIGHT
      ══════════════════════════════════════════ */}
      <div className="bg-[#1c1c1c] w-full">
        <div className="max-w-[1200px] mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between text-[12px] text-gray-400 font-medium">
          <p>
            {settings.copyrightText}
          </p>
          <div className="flex items-center gap-6 mt-4 md:mt-0">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/terms-and-conditions" className="hover:text-white transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>


    </footer>
  );
}
